/**
 * Fantasy Console 0 - Piano Roll Component
 *
 * Canvas-based note and volume editor for SFX.
 * Top section: pitch grid (32 columns x 64 rows)
 * Bottom section: volume bars (32 columns)
 */

import { useRef, useEffect, useCallback } from 'react'
import type { SFX } from '@fc0/types'
import { NOTES_PER_SFX } from '@fc0/types'
import {
  getWaveformColors,
  pitchToNoteName,
  cloneSFX,
} from '../../utils/soundUtils'
import { useTheme } from '../../context/ThemeContext'
import type { ThemeColors } from '../../utils/theme'
import styles from './PianoRoll.module.css'
import editorStyles from '../../styles/editor.module.css'

// Layout constants
const GUTTER_WIDTH = 36
const COL_WIDTH = 16
const PITCH_ROWS = 64
const PITCH_ROW_HEIGHT = 7
const VOLUME_HEIGHT = 40
const VOLUME_GAP = 4
const MEASURE_HEADER = 12

const CANVAS_WIDTH = GUTTER_WIDTH + NOTES_PER_SFX * COL_WIDTH
const PITCH_HEIGHT = PITCH_ROWS * PITCH_ROW_HEIGHT
const CANVAS_HEIGHT = MEASURE_HEADER + PITCH_HEIGHT + VOLUME_GAP + VOLUME_HEIGHT

// Loop handle constants
const HANDLE_SIZE = 6

type SfxPropsUpdate = Partial<Pick<SFX, 'loop_start' | 'loop_end'>>

interface PianoRollProps {
  sfx: SFX
  selectedWaveform: number
  selectedEffect: number
  playheadPosition: number
  noteStartTime: number
  isPlaying: boolean
  selectedColumns: Set<number>
  onSfxChange: (sfx: SFX) => void
  onSfxPropsChange?: (props: SfxPropsUpdate) => void
  onNotePreview?: (pitch: number, waveform: number, volume: number) => void
  onSelectionChange?: (columns: Set<number>) => void
}

/**
 * Draw an effect indicator symbol in white above a note rectangle.
 * x,y,w,h describe the space above the note (same width, PITCH_ROW_HEIGHT tall).
 */
function drawEffectIndicator(
  ctx: CanvasRenderingContext2D,
  effect: number,
  x: number,
  y: number,
  w: number,
  h: number,
  strokeColor: string,
) {
  if (effect === 0) return

  ctx.save()
  ctx.strokeStyle = strokeColor
  ctx.lineWidth = 1
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  const cx = x + w / 2
  const cy = y + h / 2

  ctx.beginPath()
  switch (effect) {
    case 1: // Slide: right arrow
      ctx.moveTo(x + 2, cy)
      ctx.lineTo(x + w - 2, cy)
      ctx.moveTo(x + w - 5, cy - 2)
      ctx.lineTo(x + w - 2, cy)
      ctx.lineTo(x + w - 5, cy + 2)
      break
    case 2: // Vibrato: wavy line
      ctx.moveTo(x + 1, cy)
      ctx.lineTo(x + 3, cy - 2)
      ctx.lineTo(x + 6, cy)
      ctx.lineTo(x + 9, cy + 2)
      ctx.lineTo(x + 12, cy)
      ctx.lineTo(x + w - 1, cy - 2)
      break
    case 3: // Drop: down arrow
      ctx.moveTo(cx, y + 1)
      ctx.lineTo(cx, y + h - 1)
      ctx.moveTo(cx - 3, y + h - 3)
      ctx.lineTo(cx, y + h - 1)
      ctx.lineTo(cx + 3, y + h - 3)
      break
    case 4: // Fade In: left wedge <
      ctx.moveTo(x + w - 3, y + 1)
      ctx.lineTo(x + 3, cy)
      ctx.lineTo(x + w - 3, y + h - 1)
      break
    case 5: // Fade Out: right wedge >
      ctx.moveTo(x + 3, y + 1)
      ctx.lineTo(x + w - 3, cy)
      ctx.lineTo(x + 3, y + h - 1)
      break
    case 6: // Arp Fast: double caret ^^
      ctx.moveTo(x + 1, cy + 1)
      ctx.lineTo(x + 4, y + 1)
      ctx.lineTo(x + 7, cy + 1)
      ctx.moveTo(x + 7, cy + 1)
      ctx.lineTo(x + 10, y + 1)
      ctx.lineTo(x + 13, cy + 1)
      break
    case 7: // Arp Slow: single caret ^
      ctx.moveTo(x + 3, cy + 1)
      ctx.lineTo(cx, y + 1)
      ctx.lineTo(x + w - 3, cy + 1)
      break
  }
  ctx.stroke()
  ctx.restore()
}

export function PianoRoll({
  sfx,
  selectedWaveform,
  selectedEffect,
  playheadPosition,
  noteStartTime,
  isPlaying,
  selectedColumns,
  onSfxChange,
  onSfxPropsChange,
  onNotePreview,
  onSelectionChange,
}: PianoRollProps) {
  const { theme, colors } = useTheme()
  const colorsRef = useRef<ThemeColors>(colors)
  colorsRef.current = colors
  const waveformColorsRef = useRef(getWaveformColors(theme.isDark))
  waveformColorsRef.current = getWaveformColors(theme.isDark)

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const isDrawingRef = useRef(false)
  const localSfxRef = useRef<SFX | null>(null)
  const drawRegionRef = useRef<'pitch' | 'volume' | null>(null)
  const hoverInfoRef = useRef<{ col: number; pitch: number } | null>(null)
  const loopDragRef = useRef<'start' | 'end' | null>(null)
  const isSelectingRef = useRef(false)
  const selectionStartRef = useRef<number>(-1)
  const rafIdRef = useRef<number>(0)
  const lastPreviewRef = useRef<{ col: number; pitch: number } | null>(null)

  // Keep refs in sync with props
  const sfxRef = useRef(sfx)
  sfxRef.current = sfx
  const waveformRef = useRef(selectedWaveform)
  waveformRef.current = selectedWaveform
  const effectRef = useRef(selectedEffect)
  effectRef.current = selectedEffect
  const playheadRef = useRef(playheadPosition)
  playheadRef.current = playheadPosition
  const noteStartTimeRef = useRef(noteStartTime)
  noteStartTimeRef.current = noteStartTime
  const isPlayingRef = useRef(isPlaying)
  isPlayingRef.current = isPlaying
  const selectedColumnsRef = useRef(selectedColumns)
  selectedColumnsRef.current = selectedColumns

  const render = useCallback((smoothPlayhead?: number) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const currentSfx = localSfxRef.current || sfxRef.current
    const currentSelected = selectedColumnsRef.current
    const c = colorsRef.current
    const wfColors = waveformColorsRef.current

    // Clear
    ctx.fillStyle = c.canvasBg
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

    // --- Measure Header ---
    ctx.fillStyle = c.canvasGridBgAlt
    ctx.fillRect(0, 0, CANVAS_WIDTH, MEASURE_HEADER)
    ctx.fillStyle = c.textSecondary
    ctx.font = '10px monospace'
    ctx.textAlign = 'center'
    for (let m = 0; m < 4; m++) {
      const mx = GUTTER_WIDTH + m * 8 * COL_WIDTH
      ctx.fillText(`${m + 1}`, mx, 9)
    }

    const gridTop = MEASURE_HEADER

    // --- Pitch Grid ---

    // Gutter background
    ctx.fillStyle = c.canvasGridBgAlt
    ctx.fillRect(0, gridTop, GUTTER_WIDTH, PITCH_HEIGHT)

    // Octave labels in gutter
    ctx.fillStyle = c.textSecondary
    ctx.font = '10px monospace'
    ctx.textAlign = 'right'
    for (let octave = 0; octave < 6; octave++) {
      const pitch = octave * 12
      const y = gridTop + PITCH_HEIGHT - pitch * PITCH_ROW_HEIGHT
      ctx.fillText(`C-${octave + 1}`, GUTTER_WIDTH - 4, y - 1)
    }

    // Grid lines
    ctx.lineWidth = 1

    // Horizontal octave lines
    for (let octave = 0; octave <= 5; octave++) {
      const pitch = octave * 12
      const y = gridTop + PITCH_HEIGHT - pitch * PITCH_ROW_HEIGHT
      ctx.strokeStyle = c.borderSubtle
      ctx.beginPath()
      ctx.moveTo(GUTTER_WIDTH, y + 0.5)
      ctx.lineTo(CANVAS_WIDTH, y + 0.5)
      ctx.stroke()
    }

    // Vertical lines with measure markers
    for (let col = 0; col <= NOTES_PER_SFX; col++) {
      const x = GUTTER_WIDTH + col * COL_WIDTH
      if (col % 8 === 0) {
        ctx.strokeStyle = c.borderDefault
        ctx.lineWidth = 1.5
      } else if (col % 4 === 0) {
        ctx.strokeStyle = c.borderSubtle
        ctx.lineWidth = 1
      } else {
        ctx.strokeStyle = c.canvasGridFine
        ctx.lineWidth = 1
      }
      ctx.beginPath()
      ctx.moveTo(x + 0.5, gridTop)
      ctx.lineTo(x + 0.5, gridTop + PITCH_HEIGHT)
      ctx.stroke()
    }
    ctx.lineWidth = 1

    // Loop region overlay (pitch)
    if (currentSfx.loop_start < currentSfx.loop_end) {
      const loopX = GUTTER_WIDTH + currentSfx.loop_start * COL_WIDTH
      const loopW =
        (currentSfx.loop_end - currentSfx.loop_start + 1) * COL_WIDTH
      ctx.fillStyle = c.accentPrimaryAlpha08
      ctx.fillRect(loopX, gridTop, loopW, PITCH_HEIGHT)

      // Loop markers
      ctx.strokeStyle = c.accentPrimaryAlpha30
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(loopX + 0.5, gridTop)
      ctx.lineTo(loopX + 0.5, gridTop + PITCH_HEIGHT)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(loopX + loopW + 0.5, gridTop)
      ctx.lineTo(loopX + loopW + 0.5, gridTop + PITCH_HEIGHT)
      ctx.stroke()

      // Draggable triangle handles at top
      ctx.fillStyle = c.accentPrimaryAlpha60
      // Start handle
      ctx.beginPath()
      ctx.moveTo(loopX, gridTop)
      ctx.lineTo(loopX + HANDLE_SIZE, gridTop)
      ctx.lineTo(loopX, gridTop + HANDLE_SIZE)
      ctx.closePath()
      ctx.fill()
      // End handle
      const endX = loopX + loopW
      ctx.beginPath()
      ctx.moveTo(endX, gridTop)
      ctx.lineTo(endX - HANDLE_SIZE, gridTop)
      ctx.lineTo(endX, gridTop + HANDLE_SIZE)
      ctx.closePath()
      ctx.fill()

      ctx.lineWidth = 1
    }

    // Selection overlay (pitch)
    if (currentSelected.size > 0) {
      ctx.fillStyle = c.overlayGhost
      for (const col of currentSelected) {
        const x = GUTTER_WIDTH + col * COL_WIDTH
        ctx.fillRect(x, gridTop, COL_WIDTH, PITCH_HEIGHT)
      }
    }

    // Draw notes
    for (let col = 0; col < NOTES_PER_SFX; col++) {
      const note = currentSfx.notes[col]
      if (note.volume > 0) {
        const x = GUTTER_WIDTH + col * COL_WIDTH
        const y = gridTop + PITCH_HEIGHT - (note.pitch + 1) * PITCH_ROW_HEIGHT
        const color = wfColors[note.waveform] || wfColors[0]

        // Note brightness based on volume
        const alpha = 0.4 + (note.volume / 7) * 0.6
        ctx.globalAlpha = alpha
        ctx.fillStyle = color
        ctx.fillRect(x + 1, y, COL_WIDTH - 2, PITCH_ROW_HEIGHT)

        // Note border
        ctx.strokeStyle = c.overlaySubtle
        ctx.lineWidth = 0.5
        ctx.strokeRect(x + 1, y, COL_WIDTH - 2, PITCH_ROW_HEIGHT)

        ctx.globalAlpha = 1

        // Effect indicator (drawn above the note)
        drawEffectIndicator(
          ctx,
          note.effect,
          x + 1,
          y - PITCH_ROW_HEIGHT,
          COL_WIDTH - 2,
          PITCH_ROW_HEIGHT,
          c.overlayMediumStrong,
        )
      }
    }

    // Hover highlight
    const hover = hoverInfoRef.current
    if (hover && !isDrawingRef.current) {
      const hx = GUTTER_WIDTH + hover.col * COL_WIDTH
      ctx.fillStyle = c.overlayWhisper
      ctx.fillRect(hx, gridTop, COL_WIDTH, PITCH_HEIGHT)

      // Show note name in gutter
      ctx.fillStyle = c.canvasHoverText
      ctx.font = '10px monospace'
      ctx.textAlign = 'right'
      const hy = gridTop + PITCH_HEIGHT - (hover.pitch + 0.5) * PITCH_ROW_HEIGHT
      ctx.fillText(pitchToNoteName(hover.pitch), GUTTER_WIDTH - 4, hy + 3)
    }

    // Smooth playhead on pitch grid
    const ph =
      smoothPlayhead !== undefined ? smoothPlayhead : playheadRef.current
    if (ph >= 0 && ph < NOTES_PER_SFX) {
      const px = GUTTER_WIDTH + ph * COL_WIDTH
      ctx.strokeStyle = c.overlayStrong
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(px, gridTop)
      ctx.lineTo(px, gridTop + PITCH_HEIGHT)
      ctx.stroke()
      ctx.lineWidth = 1
    }

    // --- Volume Bars ---
    const volY = gridTop + PITCH_HEIGHT + VOLUME_GAP

    // Volume background
    ctx.fillStyle = c.canvasGridBgAlt
    ctx.fillRect(GUTTER_WIDTH, volY, CANVAS_WIDTH - GUTTER_WIDTH, VOLUME_HEIGHT)

    // Volume label
    ctx.fillStyle = c.textSecondary
    ctx.font = '10px monospace'
    ctx.textAlign = 'right'
    ctx.fillText('Vol', GUTTER_WIDTH - 4, volY + VOLUME_HEIGHT / 2 + 3)

    // Vertical lines for volume area
    for (let col = 0; col <= NOTES_PER_SFX; col++) {
      const x = GUTTER_WIDTH + col * COL_WIDTH
      if (col % 8 === 0) {
        ctx.strokeStyle = c.borderDefault
        ctx.lineWidth = 1.5
      } else if (col % 4 === 0) {
        ctx.strokeStyle = c.borderSubtle
        ctx.lineWidth = 1
      } else {
        ctx.strokeStyle = c.canvasGridFine
        ctx.lineWidth = 1
      }
      ctx.beginPath()
      ctx.moveTo(x + 0.5, volY)
      ctx.lineTo(x + 0.5, volY + VOLUME_HEIGHT)
      ctx.stroke()
    }
    ctx.lineWidth = 1

    // Loop region overlay (volume)
    if (currentSfx.loop_start < currentSfx.loop_end) {
      const loopX = GUTTER_WIDTH + currentSfx.loop_start * COL_WIDTH
      const loopW =
        (currentSfx.loop_end - currentSfx.loop_start + 1) * COL_WIDTH
      ctx.fillStyle = c.accentPrimaryAlpha08
      ctx.fillRect(loopX, volY, loopW, VOLUME_HEIGHT)

      ctx.strokeStyle = c.accentPrimaryAlpha30
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(loopX + 0.5, volY)
      ctx.lineTo(loopX + 0.5, volY + VOLUME_HEIGHT)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(loopX + loopW + 0.5, volY)
      ctx.lineTo(loopX + loopW + 0.5, volY + VOLUME_HEIGHT)
      ctx.stroke()
      ctx.lineWidth = 1
    }

    // Selection overlay (volume)
    if (currentSelected.size > 0) {
      ctx.fillStyle = c.overlayGhost
      for (const col of currentSelected) {
        const x = GUTTER_WIDTH + col * COL_WIDTH
        ctx.fillRect(x, volY, COL_WIDTH, VOLUME_HEIGHT)
      }
    }

    // Draw volume bars
    for (let col = 0; col < NOTES_PER_SFX; col++) {
      const note = currentSfx.notes[col]
      if (note.volume > 0) {
        const x = GUTTER_WIDTH + col * COL_WIDTH
        const barHeight = (note.volume / 7) * VOLUME_HEIGHT
        const barY = volY + VOLUME_HEIGHT - barHeight
        const color = wfColors[note.waveform] || wfColors[0]

        ctx.fillStyle = color
        ctx.globalAlpha = 0.7
        ctx.fillRect(x + 1, barY, COL_WIDTH - 2, barHeight)
        ctx.globalAlpha = 1
      }
    }

    // Smooth playhead on volume area
    if (ph >= 0 && ph < NOTES_PER_SFX) {
      const px = GUTTER_WIDTH + ph * COL_WIDTH
      ctx.strokeStyle = c.overlayStrong
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(px, volY)
      ctx.lineTo(px, volY + VOLUME_HEIGHT)
      ctx.stroke()
      ctx.lineWidth = 1
    }
  }, [])

  // Smooth playhead animation loop
  useEffect(() => {
    if (!isPlaying) {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = 0
      }
      render()
      return
    }

    const animate = () => {
      const currentSfx = sfxRef.current
      const msPerNote = (currentSfx.speed / 60) * 1000
      const elapsed = performance.now() - noteStartTimeRef.current
      const fraction = Math.min(elapsed / msPerNote, 1)
      const smooth = playheadRef.current + fraction
      render(smooth)
      rafIdRef.current = requestAnimationFrame(animate)
    }

    rafIdRef.current = requestAnimationFrame(animate)
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = 0
      }
    }
  }, [isPlaying, render])

  // Render on every relevant change (when not in animation mode)
  useEffect(() => {
    if (!isPlaying) {
      render()
    }
  }, [sfx, playheadPosition, selectedColumns, isPlaying, render])

  // Re-render when theme changes
  useEffect(() => {
    render()
  }, [colors, render])

  const getCanvasPosition = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current
      if (!canvas) return null
      const rect = canvas.getBoundingClientRect()
      const scaleX = CANVAS_WIDTH / rect.width
      const scaleY = CANVAS_HEIGHT / rect.height
      const x = (e.clientX - rect.left) * scaleX
      const y = (e.clientY - rect.top) * scaleY
      return { x, y }
    },
    [],
  )

  const getColumnAndPitch = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const pos = getCanvasPosition(e)
      if (!pos) return null
      const { x, y } = pos
      const gridTop = MEASURE_HEADER

      if (x < GUTTER_WIDTH) return null

      const col = Math.floor((x - GUTTER_WIDTH) / COL_WIDTH)
      if (col < 0 || col >= NOTES_PER_SFX) return null

      const volY = gridTop + PITCH_HEIGHT + VOLUME_GAP

      if (y >= gridTop && y < gridTop + PITCH_HEIGHT) {
        const pitch = Math.floor(
          (gridTop + PITCH_HEIGHT - y) / PITCH_ROW_HEIGHT,
        )
        return {
          region: 'pitch' as const,
          col,
          pitch: Math.max(0, Math.min(63, pitch)),
        }
      } else if (y >= volY && y < volY + VOLUME_HEIGHT) {
        const volume = Math.round((1 - (y - volY) / VOLUME_HEIGHT) * 7)
        return {
          region: 'volume' as const,
          col,
          volume: Math.max(0, Math.min(7, volume)),
        }
      }

      return null
    },
    [getCanvasPosition],
  )

  // Check if a mouse position is on a loop handle
  const getLoopHandle = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>): 'start' | 'end' | null => {
      const pos = getCanvasPosition(e)
      if (!pos) return null
      const currentSfx = sfxRef.current
      if (currentSfx.loop_start >= currentSfx.loop_end) return null

      const { x, y } = pos
      const gridTop = MEASURE_HEADER
      if (y > gridTop + HANDLE_SIZE * 2) return null

      const startX = GUTTER_WIDTH + currentSfx.loop_start * COL_WIDTH
      const endX = GUTTER_WIDTH + (currentSfx.loop_end + 1) * COL_WIDTH

      if (
        Math.abs(x - startX) < HANDLE_SIZE + 3 &&
        y >= gridTop &&
        y < gridTop + HANDLE_SIZE * 2
      ) {
        return 'start'
      }
      if (
        Math.abs(x - endX) < HANDLE_SIZE + 3 &&
        y >= gridTop &&
        y < gridTop + HANDLE_SIZE * 2
      ) {
        return 'end'
      }
      return null
    },
    [getCanvasPosition],
  )

  const applyEdit = useCallback(
    (
      col: number,
      region: 'pitch' | 'volume',
      pitch?: number,
      volume?: number,
    ) => {
      const currentSfx = localSfxRef.current
      if (!currentSfx) return

      const note = currentSfx.notes[col]

      if (region === 'pitch' && pitch !== undefined) {
        note.pitch = pitch
        note.waveform = waveformRef.current
        note.effect = effectRef.current
        if (note.volume === 0) {
          note.volume = 5
        }
        const last = lastPreviewRef.current
        if (!last || last.col !== col || last.pitch !== pitch) {
          lastPreviewRef.current = { col, pitch }
          onNotePreview?.(pitch, waveformRef.current, note.volume)
        }
      } else if (region === 'volume' && volume !== undefined) {
        note.volume = volume
      }

      render()
    },
    [render, onNotePreview],
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault()

      // Check for loop handle drag
      const handle = getLoopHandle(e)
      if (handle && e.button === 0) {
        loopDragRef.current = handle
        return
      }

      const hit = getColumnAndPitch(e)
      if (!hit) return

      // Shift+click: selection mode
      if (e.shiftKey && e.button === 0) {
        isSelectingRef.current = true
        selectionStartRef.current = hit.col
        onSelectionChange?.(new Set([hit.col]))
        return
      }

      // Normal click clears selection
      if (selectedColumnsRef.current.size > 0) {
        onSelectionChange?.(new Set())
      }

      // Clone SFX for local mutation
      localSfxRef.current = cloneSFX(sfxRef.current)
      isDrawingRef.current = true
      drawRegionRef.current = hit.region

      if (e.button === 2) {
        localSfxRef.current.notes[hit.col].volume = 0
        render()
      } else {
        if (hit.region === 'pitch') {
          applyEdit(hit.col, 'pitch', hit.pitch)
        } else {
          applyEdit(hit.col, 'volume', undefined, hit.volume)
        }
      }
    },
    [getColumnAndPitch, getLoopHandle, applyEdit, render, onSelectionChange],
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      // Loop handle drag
      if (loopDragRef.current) {
        const pos = getCanvasPosition(e)
        if (!pos) return
        const col = Math.floor((pos.x - GUTTER_WIDTH) / COL_WIDTH)
        const clampedCol = Math.max(0, Math.min(NOTES_PER_SFX - 1, col))
        if (loopDragRef.current === 'start') {
          onSfxPropsChange?.({ loop_start: clampedCol })
        } else {
          onSfxPropsChange?.({ loop_end: clampedCol })
        }
        return
      }

      // Selection drag
      if (isSelectingRef.current) {
        const hit = getColumnAndPitch(e)
        if (!hit) return
        const start = Math.min(selectionStartRef.current, hit.col)
        const end = Math.max(selectionStartRef.current, hit.col)
        const newSel = new Set<number>()
        for (let c = start; c <= end; c++) newSel.add(c)
        onSelectionChange?.(newSel)
        return
      }

      // Hover tracking
      const hit = getColumnAndPitch(e)
      if (hit && hit.region === 'pitch') {
        hoverInfoRef.current = { col: hit.col, pitch: hit.pitch }
      } else {
        hoverInfoRef.current = null
      }

      if (!isDrawingRef.current || !localSfxRef.current) {
        if (!isPlayingRef.current) render()
        return
      }

      if (!hit) return

      if (e.buttons === 2) {
        localSfxRef.current.notes[hit.col].volume = 0
        render()
      } else if (drawRegionRef.current === 'pitch' && hit.region === 'pitch') {
        applyEdit(hit.col, 'pitch', hit.pitch)
      } else if (
        drawRegionRef.current === 'volume' &&
        hit.region === 'volume'
      ) {
        applyEdit(hit.col, 'volume', undefined, hit.volume)
      }
    },
    [
      getColumnAndPitch,
      getCanvasPosition,
      applyEdit,
      render,
      onSfxPropsChange,
      onSelectionChange,
    ],
  )

  const handleMouseUp = useCallback(() => {
    if (loopDragRef.current) {
      loopDragRef.current = null
      return
    }
    if (isSelectingRef.current) {
      isSelectingRef.current = false
      return
    }
    if (isDrawingRef.current && localSfxRef.current) {
      onSfxChange(localSfxRef.current)
      localSfxRef.current = null
    }
    isDrawingRef.current = false
    drawRegionRef.current = null
    lastPreviewRef.current = null
  }, [onSfxChange])

  const handleMouseLeave = useCallback(() => {
    hoverInfoRef.current = null
    if (!isPlayingRef.current) render()
  }, [render])

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
  }, [])

  // Global mouseup listener
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (loopDragRef.current) {
        loopDragRef.current = null
        return
      }
      if (isSelectingRef.current) {
        isSelectingRef.current = false
        return
      }
      if (isDrawingRef.current && localSfxRef.current) {
        onSfxChange(localSfxRef.current)
        localSfxRef.current = null
      }
      isDrawingRef.current = false
      drawRegionRef.current = null
      lastPreviewRef.current = null
    }

    window.addEventListener('mouseup', handleGlobalMouseUp)
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp)
  }, [onSfxChange])

  return (
    <div className={editorStyles.panelContainer}>
      <div className={editorStyles.panelHeader}>
        <span className={editorStyles.panelLabel}>Sound Editor</span>
      </div>
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className={styles.canvas}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onContextMenu={handleContextMenu}
      />
      <div className={styles.legend}>
        <span className={styles.legendItem}>Click: place note</span>
        <span className={styles.legendItem}>Right-click: erase</span>
        <span className={styles.legendItem}>Shift+drag: select</span>
      </div>
    </div>
  )
}
